import { attributes } from '../../core/ids-attributes';
import { customElement, scss } from '../../core/ids-decorators';
import { parseNumberWithUnits } from '../../utils/ids-dom-utils/ids-dom-utils';
import { isValidInteger } from '../../utils/ids-number-utils/ids-number-utils';
import { stringToBool } from '../../utils/ids-string-utils/ids-string-utils';

import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import IdsFormInputMixin from '../../mixins/ids-form-input-mixin/ids-form-input-mixin';
import IdsLabelStateMixin from '../../mixins/ids-label-state-mixin/ids-label-state-mixin';
import IdsLocaleMixin from '../../mixins/ids-locale-mixin/ids-locale-mixin';
import IdsClearableMixin from '../../mixins/ids-clearable-mixin/ids-clearable-mixin';
import IdsDirtyTrackerMixin from '../../mixins/ids-dirty-tracker-mixin/ids-dirty-tracker-mixin';
import IdsValidationMixin from '../../mixins/ids-validation-mixin/ids-validation-mixin';
import type { IdsValidationErrorMessage } from '../../mixins/ids-validation-mixin/ids-validation-mixin';
import IdsLabelPositionMixin from '../../mixins/ids-label-position-mixin/ids-label-position-mixin';
import IdsAutoSelectMixin from '../../mixins/ids-autoselect-mixin/ids-autoselect-mixin';
import IdsElement from '../../core/ids-element';

import '../ids-icon/ids-icon';
import '../ids-text/ids-text';
import '../ids-trigger-field/ids-trigger-field';

import { LABEL_WRAPS, MARGIN_BLOCK_END_SIZES, MarginBlockEndKey } from '../ids-input/ids-input-attributes';

import styles from './ids-textarea.scss';
import type IdsText from '../ids-text/ids-text';

// Setting defaults label wrap
type IdsTextareaLabelWrap = 'ellipsis' | 'wrap' | 'ellipsis-no-stretch' | 'wrap-no-stretch';

const Base = IdsAutoSelectMixin(
  IdsDirtyTrackerMixin(
    IdsLocaleMixin(
      IdsLabelStateMixin(
        IdsValidationMixin(
          IdsClearableMixin(
            IdsFormInputMixin(
              IdsLabelPositionMixin(
                IdsEventsMixin(
                  IdsElement
                )
              )
            )
          )
        )
      )
    )
  )
);

// Textarea id
const ID = 'ids-textarea-id';

// Setting defaults sizes
const SIZES: Record<string, string> = {
  default: 'md',
  sm: 'sm',
  md: 'md',
  lg: 'lg',
  full: 'full'
};

// Setting defaults text-align
const TEXT_ALIGN: Record<string, string> = {
  default: 'left',
  left: 'left',
  center: 'center',
  right: 'right'
};

export const setSizeAttr = (name: string, el: IdsTextarea, value: string | null, propTargetEl?: HTMLElement) => {
  if (value) {
    el.setAttribute(name, value.toString());
    el.container?.classList.add(`has-${name}`);
    if (propTargetEl) propTargetEl.style.setProperty(name, parseNumberWithUnits(value));
  } else {
    el.removeAttribute(name);
    el.container?.classList.remove(`has-${name}`);
    if (propTargetEl) propTargetEl.style.removeProperty(name);
  }
};

export type IdsTextareaResizeSetting = boolean | 'x' | 'y' | 'both';

/**
 * IDS Textarea Component
 * @type {IdsTextarea}
 * @inherits IdsElement
 * @mixes IdsEventsMixin
 * @mixes IdsFormInputMixin
 * @mixes IdsLabelPositionMixin
 * @mixes IdsClearableMixin
 * @mixes IdsDirtyTrackerMixin
 * @mixes IdsLabelStateMixin
 * @mixes IdsLocaleMixin
 * @mixes IdsValidationMixin
 * @mixes IdsAutoSelectMixin
 * @part container - the overall container
 * @part textarea - the textarea element
 * @part label - the label element
 */
@customElement('ids-textarea')
@scss(styles)
export default class IdsTextarea extends Base {
  /**
   * Call the constructor and then initialize
   */
  constructor() {
    super();
  }

  autogrowProcessing = false;

  isSafari = false;

  isFormComponent = true;

  /**
   * Return the attributes we handle as getters/setters
   * @returns {Array<string>} The attributes in an array
   */
  static get attributes(): Array<string> {
    return [
      ...super.attributes,
      attributes.AUTOGROW,
      attributes.AUTOSELECT,
      attributes.CHAR_MAX_TEXT,
      attributes.CHAR_REMAINING_TEXT,
      attributes.CHARACTER_COUNTER,
      attributes.DISABLED,
      attributes.MAX_HEIGHT,
      attributes.MAX_WIDTH,
      attributes.MAXLENGTH,
      attributes.MIN_HEIGHT,
      attributes.MIN_WIDTH,
      attributes.MARGIN_BLOCK_END,
      attributes.NO_MARGINS,
      attributes.PLACEHOLDER,
      attributes.PRINTABLE,
      attributes.SIZE,
      attributes.READONLY,
      attributes.RESIZABLE,
      attributes.ROWS,
      attributes.TEXT_ALIGN,
      attributes.TOOLTIP,
      attributes.VALUE
    ];
  }

  /**
   * Custom Element `connectedCallback` implementation
   * @returns {void}
   */
  connectedCallback(): void {
    super.connectedCallback();
    this.setInitialConstraint();
    this.#attachEventHandlers();

    // Initialize line clamp on label text
    if (this.lineClamp) {
      const idsText = this.container?.querySelector('ids-text');
      idsText?.setAttribute(attributes.LINE_CLAMP, `${this.lineClamp}`);
      if (this.label) {
        idsText?.setAttribute(attributes.TOOLTIP, this.label);
      }
    }
  }

  /**
   * Custom Element `disconnectedCallback` implementation
   * @returns {void}
   */
  disconnectedCallback(): void {
    super.disconnectedCallback();
  }

  onLocaleChange = () => {
    this.updateCounter();
  };

  /**
   * Create the Template for the contents
   * @returns {string} The template
   */
  template(): string {
    let textareaState = stringToBool(this.readonly) ? ' readonly' : '';
    textareaState = stringToBool(this.disabled) ? ' disabled' : textareaState;

    return `
      <div class="ids-textarea${textareaState}" part="container">
${this.templateContent()}
      </div>
    `;
  }

  /**
   * Returns the inner template content without the container wrapper
   * @returns {string} inner template HTML
   */
  private templateContent(): string {
    // Textarea
    const value = this.value || '';
    const rows = this.rows ? ` rows="${this.rows}"` : '';
    const ariaLabel = this.hasAttribute(attributes.LABEL_STATE) && this.label ? `aria-label="${this.label}"` : '';
    const hiddenLabelCss = !this.label.length || this.labelState === 'hidden' ? ' empty' : '';
    const requiredLabelCss = !this.labelRequired ? ' no-required-indicator' : '';
    const maxlength = this.maxlength ? ` maxlength="${this.maxlength}"` : '';
    const placeholder = this.placeholder ? ` placeholder="${this.placeholder}"` : '';
    const isPrintable = stringToBool(this.printable) || this.printable === null;
    const printable = isPrintable ? `<span class="textarea-print">${value}</span>` : '';
    const isCounter = (this.characterCounter && this.maxlength);
    const counter = isCounter ? '<span class="textarea-character-counter"></span>' : '';
    let textareaState = stringToBool(this.readonly) ? ' readonly' : '';
    textareaState = stringToBool(this.disabled) ? ' disabled' : textareaState;
    let textareaClass = `textarea-field ${this.textAlign}`;
    textareaClass += stringToBool(this.resizable) ? ' resizable' : '';
    textareaClass = ` class="${textareaClass}"`;

    // Handle label wrap
    const labelTextAttrs = [];
    if (this.labelWrap === 'ellipsis' || this.labelWrap === 'ellipsis-no-stretch') {
      labelTextAttrs.push('overflow="ellipsis"');
      const tooltipVal = this.getAttribute(attributes.TOOLTIP);
      labelTextAttrs.push(`tooltip="${tooltipVal && tooltipVal !== 'true' ? tooltipVal : 'true'}"`);
    }
    const labelTextAttributes = labelTextAttrs.length > 0 ? ` ${labelTextAttrs.join(' ')}` : '';

    const label = `
      <label for="${ID}" class="ids-label-text${requiredLabelCss}${hiddenLabelCss}">
        <ids-text part="label" label ${textareaState} color-unset${labelTextAttributes}>${this.label}</ids-text>
      </label>
    `;
    const fieldAndCounter = `
      <div class="field-container ${this.size}">
        <textarea part="textarea" id="${ID}"${textareaClass}${placeholder}${textareaState}${maxlength}${rows}${ariaLabel} value="${value}"></textarea>
      </div>
      ${counter}
    `;

    const isInlineStart = this.labelPosition === 'inline-start';

    if (isInlineStart) {
      return `
      <slot class="hidden"></slot>
      ${label}
      ${printable}
      <div class="textarea-field-group">
        ${fieldAndCounter}
      </div>
      `;
    }

    return `
      <slot class="hidden"></slot>
      ${label}
      ${printable}
      ${fieldAndCounter}
    `;
  }

  /**
   * Get the inner ids-text element which contains the label text
   * @returns {IdsText | null} the label text element
   */
  get labelTextEl(): IdsText | null {
    return this.shadowRoot?.querySelector<IdsText>('label ids-text') ?? null;
  }

  set label(value: string) {
    super.label = value;
    this.updateLabelTooltip();
  }

  get label() {
    return super.label;
  }

  /**
   * Get reference to this component's inner text input element
   * @returns {HTMLTextAreaElement | null} the textarea element
   */
  get input(): HTMLTextAreaElement | null {
    return this.container?.querySelector<HTMLTextAreaElement>('textarea') ?? null;
  }

  /**
   * Get the inner `textarea` element
   * @returns {HTMLInputElement | HTMLTextAreaElement | null} the textarea element
   * @see IdsFormInputMixin.formInput
   */
  get formInput(): HTMLInputElement | HTMLTextAreaElement | null {
    return this.input;
  }

  /**
   * Get the element that wraps the input and any triggering elements or icons
   * @returns {HTMLElement | null} the field container element
   */
  get fieldContainer(): HTMLElement | null {
    return this.container?.querySelector('.field-container') || null;
  }

  /**
   * Set state for disabled or readonly
   * @private
   * @param {string} prop The property.
   * @returns {void}
   */
  setTextareaState(prop: string): void {
    if (prop === attributes.READONLY || prop === attributes.DISABLED) {
      if (!this.shadowRoot) {
        return;
      }

      const msgNodes = [].slice.call(this.shadowRoot?.querySelectorAll('.validation-message'));
      const options = {
        prop1: prop,
        prop2: prop !== attributes.READONLY ? attributes.READONLY : attributes.DISABLED,
        val: stringToBool((this as any)[prop])
      };
      if (options.val) {
        this.input?.removeAttribute(options.prop2);
        this.container?.classList.remove(options.prop2);
        this.container?.querySelector('ids-text')?.removeAttribute(options.prop2);
        msgNodes.forEach((x: HTMLElement) => x.classList.remove(options.prop2));

        this.input?.setAttribute(options.prop1, 'true');
        this.container?.classList.add(options.prop1);
        this.container?.querySelector('ids-text')?.setAttribute(options.prop1, 'true');
        msgNodes.forEach((x: HTMLElement) => x.classList.add(options.prop1));
      } else {
        this.input?.removeAttribute(options.prop1);
        this.container?.classList.remove(options.prop1);
        this.container?.querySelector('ids-text')?.removeAttribute(options.prop1);
        msgNodes.forEach((x: HTMLElement) => x.classList.remove(options.prop1));
      }
    }
  }

  /**
   * Handle autogrow
   * @private
   * @returns {void}
   */
  handleAutogrow(): void {
    if (this.input) {
      if (this.autogrow) {
        this.input.style.overflowY = 'auto';
        if (this.maxWidth) this.input.style.overflowX = 'auto';
        this.setAutogrow();
      } else {
        this.input.style.overflowY = '';
        this.input.style.overflowX = '';
        if (!this.resizable) {
          this.input.style.height = '';
        }
      }
    }
  }

  /**
   * Set Browser
   * will remove this soon get environment utils setup
   * @private
   * @returns {void}
   */
  setBrowser(): void {
    const ua = navigator.userAgent || navigator.vendor || (window as any).opera;
    const browser = (s: string) => ua.toLowerCase().indexOf(s) > -1;
    this.isSafari = browser('safari') && !browser('chrome') && !browser('android');
  }

  /**
   * Get the max text value, trim extra
   * @private
   * @param {string} value of textarea
   * @returns {string} max value
   */
  getMaxValue(value: string): string {
    const max = parseInt((this.maxlength as any), 10);
    return value && max > 0 ? value.substr(0, max) : value;
  }

  /**
   * Set the label text
   * @private
   * @param {string} value of label
   * @returns {void}
   */
  setLabelText(value: string): void {
    return super.setLabelText(value, `[for="${ID}"]`);
  }

  /**
   * Set autogrow
   * @private
   * @returns {void}
   */
  private setAutogrow(): void {
    if (this.autogrow && !this.autogrowProcessing) {
      this.autogrowProcessing = true;
      this.constrainDimensions();
      this.autogrowProcessing = false;
    }
  }

  /**
   * Set initial size constraints on the textarea
   */
  private setInitialConstraint() {
    this.minHeight = this.minHeight;
    this.maxHeight = this.maxHeight;
    this.minWidth = this.minWidth;
    this.maxWidth = this.maxWidth;
    this.constrainDimensions();
  }

  /**
   * Adjust width/height based on defined values
   * @private
   */
  private constrainDimensions() {
    const minHeight = parseInt((this.minHeight as any), 10) || 0;
    const oldHeight = this.input?.offsetHeight || 0;
    this.adjustHeight(oldHeight, minHeight);
  }

  /**
   * Adjust height to given element
   * @private
   * @param {number} oldHeight old height
   * @param {number} maxHeight max height
   * @param {HTMLElement|null} input The textarea input element
   * @returns {void}
   */
  adjustHeight(oldHeight: number, maxHeight: number, input: HTMLElement | null = null): void {
    const elem = input || this.input;
    let newHeight = elem?.offsetHeight || 0;
    const scrollHeight = elem?.scrollHeight || 0;
    if (scrollHeight > newHeight) newHeight = scrollHeight;

    if (elem && typeof newHeight === 'number' && (oldHeight !== newHeight)) {
      let height = newHeight;
      if (oldHeight > newHeight) {
        elem.style.height = '5px';
        height = elem.scrollHeight;
      }
      if (this.hasAttribute(attributes.MAX_HEIGHT)) {
        const isScrollable = (maxHeight > 0 && maxHeight < height);
        elem.style.overflowY = isScrollable ? '' : 'auto';
      } else {
        elem.style.overflowY = 'hidden';
      }
      elem.style.height = `${height}px`;
    }
  }

  /**
   * Handle character-counter
   * @private
   * @returns {void}
   */
  handleCharacterCounter(): void {
    let elem = this.shadowRoot?.querySelector('.textarea-character-counter');
    if (this.characterCounter && this.maxlength) {
      if (!elem) {
        elem = document.createElement('span');
        elem.className = 'textarea-character-counter';
        const group = this.container?.querySelector('.textarea-field-group');
        (group ?? this.container)?.appendChild(elem);
      }
      this.updateCounter();
    } else {
      elem?.remove();
    }
  }

  /**
   * Handle printable
   * @private
   * @param {boolean|string|null} value of printable
   * @returns {void}
   */
  handlePrintable(value: boolean | string | null): void {
    let elem = this.shadowRoot?.querySelector('.textarea-print');
    if (stringToBool(this.printable) || value === null) {
      if (!elem) {
        elem = document.createElement('span');
        elem.className = 'textarea-print';
        elem.textContent = this.value;
        this.container?.prepend(elem);
      }
    } else {
      elem?.remove();
    }
  }

  /**
   * Handle slotchange event
   * @private
   * @returns {void}
   */
  handleSlotchangeEvent(): void {
    const slot = this.shadowRoot?.querySelector('slot');

    if (!slot) return; // Early return if slot is not found

    this.onEvent('slotchange', slot, () => {
      const textarea = this.shadowRoot?.querySelector('textarea');
      const idsTextElement = this.querySelector('ids-text');

      const noContent = textarea?.textContent === '';
      const isTooltip = idsTextElement?.id?.includes('tooltip');
      const val = slot?.assignedNodes()[0].textContent;

      this.value = (noContent && isTooltip) ? '' : this.getMaxValue(val || '');
    });
  }

  /**
   * Handle focus event
   * @private
   * @param {string | undefined | null} option If 'remove', will remove attached events
   * @returns {void}
   */
  handleTextareaFocusEvent(option: string | undefined | null = ''): void {
    if (this.input) {
      const eventName = 'focus';
      if (option === 'remove') {
        const handler = this.handledEvents?.get(eventName);
        if (handler && handler.target === this.input) {
          this.offEvent(eventName, this.input);
        }
      } else {
        this.onEvent(eventName, this.input, () => {
          setTimeout(() => { // safari has delay
            this.input?.select();
          }, 1);
        });
      }
    }
  }

  /**
   * Establish Internal Event Handlers
   * @private
   * @returns {object} The object for chaining.
   */
  handleNativeEvents(): object {
    if (this.input) {
      const events = ['propertychange', 'focus', 'select'];
      events.forEach((evt) => {
        this.onEvent(evt, this.input, (e: Event) => {
          /**
           * Trigger event on parent and compose the args
           * will fire nativeEvents.
           * @private
           * @param {object} elem Actual event
           * @param {string} value The updated element value
           */
          this.triggerEvent(`${e.type}.ids-textarea`, this, {
            detail: { elem: this, nativeEvent: e, value: this.value }
          });
        });
      });

      // suppress native input event
      this.onEvent('input', this.input, (e: InputEvent) => {
        if (e instanceof InputEvent) {
          e.stopPropagation();
        }
      });
    }
    return this;
  }

  /**
   * Handle events
   * @private
   * @returns {void}
   */
  #attachEventHandlers(): void {
    this.setBrowser();
    this.handleAutogrow();
    this.handleSlotchangeEvent();
    this.handleNativeEvents();

    this.onEvent('input', this?.input, () => {
      this.#updateValue();
    });
  }

  /**
   * Update Value attribute
   */
  #updateValue(): void {
    if (this?.getAttribute(attributes.VALUE) !== this.input?.value) {
      this.setAttribute(attributes.VALUE, this.input?.value || '');
    }
  }

  /**
   * Updates the printarea with current value.
   * @private
   * @returns {void}
   */
  updatePrintarea(): void {
    const printareaEl = this.shadowRoot?.querySelector('.textarea-print');
    if (printareaEl) {
      printareaEl.textContent = this.value;
    }
  }

  /**
   * Counts the number of line breaks in a string
   * @private
   * @param {string} s The string to test.
   * @returns {number} The number of found line countLinebreaks
   */
  countLinebreaks(s: string): number {
    return (s.match(/\n/g) || []).length;
  }

  /**
   * Updates the descriptive markup (counter, etc) to notify the user how many
   * characters can be typed.
   * @private
   * @returns {void}
   */
  updateCounter(): void {
    const elem = this.shadowRoot?.querySelector('.textarea-character-counter');
    if (elem && this.maxlength) {
      const val = this.value || '';
      const linebreaks = this.isSafari ? this.countLinebreaks(val) : 0;
      const length = val.length + linebreaks;
      const max = parseInt(this.maxlength, 10);
      const remaining = (max - length);
      const almostEmpty = 'almost-empty';
      let text = this.charRemainingText.replace('{0}', remaining.toString());

      if (length >= max) {
        text = this.charMaxText.replace('{0}', max.toString());
        elem.classList.add('max-reached');
      } else {
        elem.classList.remove('max-reached');
      }

      elem.textContent = text;

      if (remaining < 10) {
        elem.classList.add(almostEmpty);
      } else {
        elem.classList.remove(almostEmpty);
      }
    }
  }

  /**
   * Set textarea height to be autogrow
   * @param {boolean | string} value If true will set `autogrow` attribute
   */
  set autogrow(value: boolean | string) {
    const val = stringToBool(value);
    if (val) {
      this.setAttribute(attributes.AUTOGROW, val.toString());
    } else {
      this.removeAttribute(attributes.AUTOGROW);
    }
    this.handleAutogrow();
  }

  /**
   * Get whether autogrow is enabled
   * @returns {boolean | string} true if autogrow is enabled
   */
  get autogrow(): boolean | string { return stringToBool(this.getAttribute(attributes.AUTOGROW)); }

  /**
   * Sets a "max-height" CSS property on the textarea
   * @param {string | null} value of `max-height` attribute
   */
  set maxHeight(value: string | null) {
    setSizeAttr(attributes.MAX_HEIGHT, this, value, this.input!);
    this.handleAutogrow();
  }

  /**
   * Get the max-height CSS property value
   * @returns {string | null} the max-height value
   */
  get maxHeight(): string | null { return this.getAttribute(attributes.MAX_HEIGHT); }

  /**
   * Sets a "max-width" CSS property on the textarea
   * @param {string | null} value of `max-width` attribute
   */
  set maxWidth(value: string | null) {
    setSizeAttr(attributes.MAX_WIDTH, this, value, this.input!);
    if (this.fieldContainer) {
      if (value) {
        this.fieldContainer.style.setProperty(attributes.MAX_WIDTH, parseNumberWithUnits(value));
      } else {
        this.fieldContainer.style.removeProperty(attributes.MAX_WIDTH);
      }
    }
  }

  /**
   * Get the max-width CSS property value
   * @returns {string | null} the max-width value
   */
  get maxWidth(): string | null { return this.getAttribute(attributes.MAX_WIDTH); }

  /**
   * Sets a "min-height" CSS property on the textarea
   * @param {string | null} value of `min-height` attribute
   */
  set minHeight(value: string | null) {
    setSizeAttr(attributes.MIN_HEIGHT, this, value, this.input!);
    this.handleAutogrow();
  }

  /**
   * Get the min-height CSS property value
   * @returns {string | null} the min-height value
   */
  get minHeight(): string | null { return this.getAttribute(attributes.MIN_HEIGHT); }

  /**
   * Sets a "min-width" CSS property on the textarea
   * @param {string | null} value of `min-width` attribute
   */
  set minWidth(value: string | null) {
    setSizeAttr(attributes.MIN_WIDTH, this, value, this.input!);
    if (this.fieldContainer) {
      if (value) {
        this.fieldContainer.style.setProperty(attributes.MIN_WIDTH, parseNumberWithUnits(value));
      } else {
        this.fieldContainer.style.removeProperty(attributes.MIN_WIDTH);
      }
    }
  }

  /**
   * Get the min-width CSS property value
   * @returns {string | null} the min-width value
   */
  get minWidth(): string | null { return this.getAttribute(attributes.MIN_WIDTH); }

  /**
   * Sets a "height" CSS property on the textarea
   * @param {string | null} value of `height` attribute
   */
  set height(value: string | null) {
    if (value) {
      this.setAttribute(attributes.HEIGHT, value.toString());
      this.style.setProperty('--ids-textarea-height', value);
    } else {
      this.removeAttribute(attributes.HEIGHT);
      this.style.removeProperty('--ids-textarea-height');
    }
  }

  /**
   * Get the height CSS property value
   * @returns {string | null} the height value
   */
  get height(): string | null { return this.getAttribute(attributes.HEIGHT); }

  /**
   * Sets the margin-block-end spacing token on the inner .ids-textarea container.
   * Accepts token shorthand keys (e.g. 'sm', 'lg') that map to IDS spacing CSS variables.
   * @param {MarginBlockEndKey | null} value spacing token key, or null to remove
   */
  set marginBlockEnd(value: MarginBlockEndKey | null) {
    const container = this.container;
    const prev = this.getAttribute(attributes.MARGIN_BLOCK_END) as MarginBlockEndKey | null;
    if (prev) container?.classList.remove(`margin-block-end-${prev}`);

    if (value && Object.prototype.hasOwnProperty.call(MARGIN_BLOCK_END_SIZES, value)) {
      this.setAttribute(attributes.MARGIN_BLOCK_END, value);
      container?.classList.add(`margin-block-end-${value}`);
    } else {
      this.removeAttribute(attributes.MARGIN_BLOCK_END);
    }
  }

  /**
   * Gets the margin-block-end token key
   * @returns {MarginBlockEndKey | null} the current spacing token key, or null if not set
   */
  get marginBlockEnd(): MarginBlockEndKey | null {
    const val = this.getAttribute(attributes.MARGIN_BLOCK_END) as MarginBlockEndKey | null;
    if (val && Object.prototype.hasOwnProperty.call(MARGIN_BLOCK_END_SIZES, val)) return val;
    return null;
  }

  /**
   * Sets the no margins attribute
   * @param {boolean | string} n true or false
   */
  set noMargins(n: boolean | string) {
    if (stringToBool(n)) {
      this.setAttribute(attributes.NO_MARGINS, '');
      this.container?.classList.add('no-margins');
      return;
    }
    this.removeAttribute(attributes.NO_MARGINS);
    this.container?.classList.remove('no-margins');
  }

  /**
   * Get whether margins are disabled
   * @returns {boolean | string} true if margins are disabled
   */
  get noMargins(): boolean | string {
    return stringToBool(this.getAttribute(attributes.NO_MARGINS));
  }

  /**
   * Set `char-max-text` text for character counter
   * @param {string} value of the `char-max-text` property
   */
  set charMaxText(value: string) {
    if (value) {
      this.setAttribute(attributes.CHAR_MAX_TEXT, value.toString());
      return;
    }
    this.removeAttribute(attributes.CHAR_MAX_TEXT);
  }

  /**
   * Get the char-max-text for character counter
   * @returns {string} the char-max-text
   */
  get charMaxText(): string { return this.getAttribute(attributes.CHAR_MAX_TEXT) || this.localeAPI.translate('CharactersMax'); }

  /**
   * Set `char-remaining-text` text for character counter
   * @param {string} value of the `char-remaining-text` property
   */
  set charRemainingText(value: string) {
    if (value) {
      this.setAttribute(attributes.CHAR_REMAINING_TEXT, value.toString());
      return;
    }
    this.removeAttribute(attributes.CHAR_REMAINING_TEXT);
  }

  /**
   * Get the char-remaining-text for character counter
   * @returns {string} the char-remaining-text
   */
  get charRemainingText(): string {
    return this.getAttribute(attributes.CHAR_REMAINING_TEXT) || this.localeAPI.translate('CharactersLeft');
  }

  /**
   * Set the `character-counter` feature
   * @param {boolean | string} value If true will set `character-counter` attribute
   */
  set characterCounter(value: boolean | string) {
    const val = stringToBool(value);
    if (value === null || value === undefined) {
      this.removeAttribute(attributes.CHARACTER_COUNTER);
    } else {
      this.setAttribute(attributes.CHARACTER_COUNTER, val.toString());
    }
    this.handleCharacterCounter();
  }

  /**
   * Get whether character counter is enabled
   * @returns {boolean | string} true if character counter is enabled
   */
  get characterCounter(): boolean | string {
    const val = this.getAttribute(attributes.CHARACTER_COUNTER);
    return val !== null ? stringToBool(val) : true;
  }

  /**
   * Sets textarea to disabled
   * @param {boolean | string} value If true will set `disabled` attribute
   */
  set disabled(value: boolean | string) {
    const val = stringToBool(value);
    if (val) {
      this.setAttribute(attributes.DISABLED, val.toString());
      if (this.hasAttribute(attributes.READONLY)) {
        this.removeAttribute(attributes.READONLY);
        this.setTextareaState(attributes.READONLY);
      }
    } else {
      this.removeAttribute(attributes.DISABLED);
    }
    this.setTextareaState(attributes.DISABLED);
  }

  /**
   * Get whether textarea is disabled
   * @returns {boolean | string} true if disabled
   */
  get disabled(): boolean | string { return stringToBool(this.getAttribute(attributes.DISABLED)); }

  /**
   * internal reference to a label element a user provides
   */
  #labelEl?: HTMLLabelElement;

  /**
   * Get the inner `label` element
   * @returns {HTMLLabelElement | null} the label element
   */
  get labelEl(): HTMLLabelElement | null {
    return this.#labelEl || this.shadowRoot?.querySelector(`[for="${ID}"]`) || null;
  }

  /**
   * Set the `maxlength` of textarea
   * @param {string|null} value of the `maxlength` property
   */
  set maxlength(value: string | null) {
    if (!isValidInteger(value, { min: 0 })) {
      this.removeAttribute(attributes.MAXLENGTH);
      this.input?.removeAttribute(attributes.MAXLENGTH);
      this.handleCharacterCounter();
      return;
    }

    const strValue = value!.toString();
    this.setAttribute(attributes.MAXLENGTH, strValue);
    this.input?.setAttribute(attributes.MAXLENGTH, strValue);
    this.handleCharacterCounter();
  }

  /**
   * Get the maxlength of textarea
   * @returns {string | null} the maxlength value
   */
  get maxlength(): string | null {
    const value = this.getAttribute(attributes.MAXLENGTH);
    return isValidInteger(value, { min: 0 }) ? value : null;
  }

  /**
   * Set the `placeholder` of textarea
   * @param {string|null} value of the `placeholder` property
   */
  set placeholder(value: string | null) {
    if (value) {
      this.setAttribute(attributes.PLACEHOLDER, value.toString());
      this.input?.setAttribute(attributes.PLACEHOLDER, value.toString());
      return;
    }
    this.removeAttribute(attributes.PLACEHOLDER);
    this.input?.removeAttribute(attributes.PLACEHOLDER);
  }

  /**
   * Get the placeholder of textarea
   * @returns {string | null} the placeholder text
   */
  get placeholder(): string | null { return this.getAttribute(attributes.PLACEHOLDER); }

  /**
   * Set the `printable` of textarea
   * @param {boolean | string} value If true will set `printable` attribute
   */
  set printable(value: boolean | string) {
    const val = stringToBool(value);
    if (val) {
      this.setAttribute(attributes.PRINTABLE, val.toString());
    } else {
      this.removeAttribute(attributes.PRINTABLE);
    }
    this.handlePrintable(value);
  }

  /**
   * Get whether printable is enabled
   * @returns {boolean | string} true if printable is enabled
   */
  get printable(): boolean | string { return stringToBool(this.getAttribute(attributes.PRINTABLE)); }

  /**
   * Set the textarea to readonly state
   * @param {boolean | string} value If true will set `readonly` attribute
   */
  set readonly(value: boolean | string) {
    const val = stringToBool(value);
    if (val) {
      this.setAttribute(attributes.READONLY, val.toString());
      if (this.hasAttribute(attributes.DISABLED)) {
        this.removeAttribute(attributes.DISABLED);
        this.setTextareaState(attributes.DISABLED);
      }
    } else {
      this.removeAttribute(attributes.READONLY);
    }
    this.setTextareaState(attributes.READONLY);
  }

  /**
   * Get whether textarea is readonly
   * @returns {boolean | string} true if readonly
   */
  get readonly(): boolean | string { return stringToBool(this.getAttribute(attributes.READONLY)); }

  /**
   * Set the textarea to resizable state
   * @param {IdsTextareaResizeSetting|string} value If true will set `resizable` attribute
   */
  set resizable(value: IdsTextareaResizeSetting | string) {
    const isTruthy = stringToBool(value);
    if (isTruthy) {
      this.setAttribute(attributes.RESIZABLE, value.toString());
    } else {
      this.removeAttribute(attributes.RESIZABLE);
    }

    this.container?.classList.remove(
      attributes.RESIZABLE,
      `${attributes.RESIZABLE}-x`,
      `${attributes.RESIZABLE}-y`,
    );

    if (value) {
      const stringVal = value.toString();
      switch (stringVal) {
        case 'x':
          this.container?.classList.add(`${attributes.RESIZABLE}-x`);
          break;
        case 'y':
          this.container?.classList.add(`${attributes.RESIZABLE}-y`);
          break;
        case 'both':
        case 'true':
          this.container?.classList.add(attributes.RESIZABLE);
          break;
        default: // false
          break;
      }
    }
  }

  /**
   * Get whether textarea is resizable
   * @returns {IdsTextareaResizeSetting} the resizable setting
   */
  get resizable(): IdsTextareaResizeSetting { return stringToBool(this.getAttribute(attributes.RESIZABLE)); }

  /**
   * Set the rows for textarea
   * @param {string|null} value If true will set `rows` attribute
   */
  set rows(value: string | null) {
    if (value) {
      this.setAttribute(attributes.ROWS, value.toString());
      this.input?.setAttribute(attributes.ROWS, value.toString());
    } else {
      this.removeAttribute(attributes.ROWS);
      this.input?.removeAttribute(attributes.ROWS);
    }
  }

  /**
   * Get the rows for textarea
   * @returns {string | null} the rows value
   */
  get rows(): string | null { return this.getAttribute(attributes.ROWS); }

  /**
   * Set the label wrap setting of textarea
   * @param {IdsTextareaLabelWrap} value ['ellipsis', 'wrap', 'ellipsis-no-stretch', 'wrap-no-stretch']
   */
  set labelWrap(value: IdsTextareaLabelWrap) {
    if (!value || LABEL_WRAPS.indexOf(value as any) < 0) {
      this.removeAttribute(attributes.LABEL_WRAP);
      this.container?.querySelector('ids-text')?.removeAttribute('overflow');
      this.container?.querySelector('ids-text')?.removeAttribute('tooltip');
    } else {
      this.setAttribute(attributes.LABEL_WRAP, value);
      if (value === 'ellipsis' || value === 'ellipsis-no-stretch') {
        this.container?.querySelector('ids-text')?.setAttribute('overflow', 'ellipsis');
        const tooltipVal = this.getAttribute(attributes.TOOLTIP);
        this.container?.querySelector('ids-text')?.setAttribute('tooltip', tooltipVal && tooltipVal !== 'true' ? tooltipVal : 'true');
      }
    }
  }

  /**
   * Get the label wrap setting
   * @returns {IdsTextareaLabelWrap} the label wrap setting
   */
  get labelWrap(): IdsTextareaLabelWrap {
    return (this.getAttribute(attributes.LABEL_WRAP) as IdsTextareaLabelWrap) || 'wrap';
  }

  /**
   * Set the line clamp value for the label text
   * @param {string | number | null} value The number of lines to clamp
   */
  set lineClamp(value: string | number | null) {
    const idsText = this.container?.querySelector('ids-text');
    if (value) {
      this.setAttribute(attributes.LINE_CLAMP, `${value}`);
      idsText?.setAttribute(attributes.LINE_CLAMP, `${value}`);
    } else {
      this.removeAttribute(attributes.LINE_CLAMP);
      idsText?.removeAttribute(attributes.LINE_CLAMP);
    }
  }

  /**
   * Get the line clamp value
   * @returns {string | null} the line clamp value
   */
  get lineClamp(): string | null {
    return this.getAttribute(attributes.LINE_CLAMP);
  }

  /**
   * Set the size (width) of textarea
   * @param {string} value [sm, md, lg, full]
   */
  set size(value: string) {
    const fieldContainer = this.shadowRoot?.querySelector('.field-container');
    const size = SIZES[value];
    this.setAttribute(attributes.SIZE, size || SIZES.default);
    fieldContainer?.classList.remove(...Object.values(SIZES));
    fieldContainer?.classList.add(size || SIZES.default);
  }

  /**
   * Get the size (width) of textarea
   * @returns {string} the size value
   */
  get size(): string { return this.getAttribute(attributes.SIZE) || SIZES.default; }

  /**
   * Sets the text alignment
   * @param {string} value [left, center, right]
   */
  set textAlign(value: string) {
    if (value === 'start') value = 'left';
    else if (value === 'end') value = 'right';
    const textAlign = TEXT_ALIGN[value];
    this.setAttribute(attributes.TEXT_ALIGN, textAlign || TEXT_ALIGN.default);
    this.input?.classList.remove(...Object.values(TEXT_ALIGN));
    this.input?.classList.add(textAlign || TEXT_ALIGN.default);
  }

  /**
   * Get the text alignment
   * @returns {string} the text alignment value
   */
  get textAlign(): string { return this.getAttribute(attributes.TEXT_ALIGN) || TEXT_ALIGN.default; }

  /**
   * Set the `value` of textarea
   * @param {string} val the value property
   */
  set value(val: string) {
    const v = val || '';
    super.value = v;

    if (this.input && this.input.value !== v) {
      this.input.value = this.getMaxValue(v);
      this.resetDirtyTracker();
    }
    this.updateCounter();
    this.updatePrintarea();
    this.setAutogrow();
  }

  /**
   * Get the value of textarea
   * @returns {string} the textarea value
   */
  get value(): string { return this.input?.value || ''; }

  addMessage(settings: IdsValidationErrorMessage): void {
    (Base.prototype as any).addMessage.call(this, settings);
    this.#moveValidationIntoGroup();
  }

  #moveValidationIntoGroup(): void {
    const group = this.container?.querySelector<HTMLElement>('.textarea-field-group');
    if (!group) return;
    this.shadowRoot
      ?.querySelectorAll<HTMLElement>('.ids-textarea > .validation-message')
      .forEach((msg) => {
        if (msg.parentElement !== group) group.appendChild(msg);
      });
  }

  onLabelPositionChange(): void {
    if (!this.container) return;
    const renderedInlineStart = !!this.container.querySelector('.textarea-field-group');
    const isInlineStart = this.labelPosition === 'inline-start';
    if (renderedInlineStart === isInlineStart) return;

    const previousValue = this.value;
    this.#detachInputEvents();
    this.container.innerHTML = this.templateContent();

    if (this.input && previousValue) this.input.value = previousValue;
    this.handleCharacterCounter();
    this.handlePrintable(this.printable);
    this.handleAutogrow();
    this.#reattachInputEvents();
    this.#moveValidationIntoGroup();

    // Re-apply mixin state on the new input element
    if (this.validate) this.handleValidation();
    if (this.dirtyTracker) this.handleDirtyTracker();
    if (this.clearable || this.clearableForced) this.handleClearable();
    if (this.labelState && this.labelStates.includes(this.labelState)) {
      this.container.classList.add(`label-state-${this.labelState}`);
      this.setLabelText?.('');
    }
  }

  #detachInputEvents(): void {
    if (!this.input) return;
    ['propertychange', 'focus', 'select', 'input'].forEach((evt) => {
      this.offEvent(evt, this.input);
    });
  }

  #reattachInputEvents(): void {
    this.handleNativeEvents();
    this.onEvent('input', this.input, () => {
      if (this.getAttribute(attributes.VALUE) !== this.input?.value) {
        this.setAttribute(attributes.VALUE, this.input?.value || '');
      }
    });
  }

  /**
   * Handle label break changes
   * @param {string|null} newValue The new label break value
   * @param {string|null} oldValue The old label break value
   */
  onLabelBreakChange(newValue: string | null, oldValue: string | null): void {
    const container = this.container;
    if (!container) return;

    // Remove old classes
    if (oldValue) {
      container.classList.remove(`label-break-${oldValue}`);
    }

    // Add new classes
    if (newValue && newValue !== 'shrink-value') {
      container.classList.add(`label-break-${newValue}`);
    }
  }

  /**
   * Gets the label container element from the shadow DOM
   * NOTE: Overrides the mixin implementation to return the textarea-specific container
   * @returns {HTMLElement|null} The .ids-textarea container element, or null if not found
   */
  override labelContainer(): HTMLElement | null {
    return this.shadowRoot?.querySelector('.ids-textarea') || null;
  }
}
